import React, {useContext} from 'react';
import {classes, style} from "./doppe-page-cookie-banner.st.css";
import {DoppeViewerModalsController} from "../../doppe-viewer-modals-controller/doppe-viewer-modals-controller";
import {assertNotNullable} from "@wix/devzai-utils-common";
import {ModalsScopeContext, ScrollView} from "@wix/devzai-utils-react";
import {
    DoppeViewerHorizontalButton,
    DoppeViewerHorizontalButtonSize
} from "../../doppe-viewer-horizontal-button/doppe-viewer-horizontal-button";
import {
    DoppePageCookieBannerSettingsModal
} from "../doppe-page-cookie-banner-settings-modal/doppe-page-cookie-banner-settings-modal";
import {useBymoPageContextProps} from "../../../doppe-sdk";
import {DoppeCookieBannerCustomTextSource} from "../../../client-server-common/types/doppe-dto-user-settings";
import {useDoppeViewerClientServices} from "../../../client/doppe-user-app/doppe-viewer-client-services";
import {useDoppeViewerLocales} from "../../../client/doppe-viewer-locales/doppe-viewer-locales";

export const DoppePageCookieBanner = React.memo(function DoppePageCookieBanner (props: DoppePageCookieBanner.Props) {
    const {
        className,
        onClose,
        ...htmlAttributes
    } = props;

    const modalsScope = assertNotNullable(useContext(ModalsScopeContext));

    const bymoPageContextProps = useBymoPageContextProps();
    const cookieConsentService = assertNotNullable(useDoppeViewerClientServices().doppeViewerAppCookieService.cookieConsentManager);
    const doppeViewerLocales = useDoppeViewerLocales();

    const userCookieBannerSettings = bymoPageContextProps.userCookieBannerSettings;
    const userPrivacyPolicyUrl = (bymoPageContextProps.userPrivacyPolicyUrl ?? '').trim();

    const hasCustomTexts = userCookieBannerSettings?.customTextSource === DoppeCookieBannerCustomTextSource.Custom;

    const modalsController = new DoppeViewerModalsController({
        modalsScope: modalsScope
    });

    return (
        <div
            {...htmlAttributes}
            className={style(classes.root, className)}
        >

            <ScrollView scrolledContentWrapperClassName={style(classes.scrolledContentWrapper)}>
                <div
                    className={style(classes.messageContainer, {})}
                >
                    {hasCustomTexts ? userCookieBannerSettings?.customTexts?.informText : doppeViewerLocales['cookieConsentDescription']}
                    {
                        userPrivacyPolicyUrl !== '' ?
                            <a className={style(classes.privacyPolicyLink, {})} href={userPrivacyPolicyUrl} target={'_blank'} rel={'noopener'}> {doppeViewerLocales['cookieConsent.privacyPolicy']}</a> :
                            null
                    }
                </div>
            </ScrollView>
            <div className={style(classes.buttonGroup, {})}>
                <DoppeViewerHorizontalButton
                    className={style(classes.settingsButton, {})}
                    textual={true}
                    size={DoppeViewerHorizontalButtonSize.Small}
                    onClick={() => {
                        modalsController.showInnerModal({
                            content: ({modalController}) => (
                                <DoppePageCookieBannerSettingsModal
                                    onPartialApprove={() => {
                                        onClose();
                                    }}
                                    onClose={() => {modalController.closeWindow()}}
                                />
                            )
                        });
                    }}
                >
                    {hasCustomTexts ? userCookieBannerSettings?.customTexts?.settingsButtonText : doppeViewerLocales['cookieConsentSettings']}
                </DoppeViewerHorizontalButton>
                <DoppeViewerHorizontalButton size={DoppeViewerHorizontalButtonSize.Medium}
                    className={style(classes.declineButton, {})}
                    onClick={() => {cookieConsentService.denyAll(); onClose();}}
                >
                    {hasCustomTexts ? userCookieBannerSettings?.customTexts?.declineAllButtonText : doppeViewerLocales['cookieConsentDeclineAll']}
                </DoppeViewerHorizontalButton>
                <DoppeViewerHorizontalButton
                    className={style(classes.acceptButton, {})}
                    size={DoppeViewerHorizontalButtonSize.Medium}
                    onClick={() => { cookieConsentService.approveAll(); onClose();}}
                >
                    {hasCustomTexts ? userCookieBannerSettings?.customTexts?.consentButtonText : doppeViewerLocales['cookieConsentAccept']}
                </DoppeViewerHorizontalButton>
            </div>
        </div>
    );
});

export namespace DoppePageCookieBanner {

    export interface Props extends React.HTMLAttributes<any> {
        onClose: () => void;
    }
}
